import * as amplitude from "@amplitude/analytics-browser";
import LogRocket from "logrocket";
import moment from "moment";

import { WorkspacePlanTier, WorkspacePlanType } from "common/interfaces";
import { isCypress, isInternalBAIUser, isPPTAddin } from "js/config";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import { ShowSnackBar } from "js/react/components/Dialogs/SnackBar";
import { _ } from "js/vendor";
import { workspaces as workspacesApi } from "apis/callables";

const logger = getLogger(LogGroup.ANALYTICS);

// Default state
let state = {
    context: isPPTAddin ? "ppt" : "app",
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight
};

// tracking window size
const setWindowSize = () => {
    state.windowWidth = window.innerWidth;
    state.windowHeight = window.innerHeight;
};

window.addEventListener("resize", setWindowSize);

function getFullDaysBetween(startTimestamp, endTimestamp) {
    const startDate = moment(parseInt(startTimestamp)).clone().startOf("day");
    const endDate = moment(parseInt(endTimestamp)).clone().startOf("day");
    return endDate.diff(startDate, "days");
}

async function initAmplitude(user) {
    amplitude.init(window.amplitudeApiKey, user.uid, {
        autocapture: {
            elementInteractions: true,
        },
    });
    const identifyEvent = new amplitude.Identify();

    // User creation date properties are segmented this way due to Amplitude reporting restrictions
    const creationDate = moment(parseInt(user.metadata.createdAt));
    identifyEvent.set("user_created_utc_year", creationDate.year());
    identifyEvent.set("user_created_utc_month", creationDate.month());
    identifyEvent.set("user_created_utc_day", creationDate.day());

    // Onboarding survey results - may be missing or in wrong format for some users so we need validation checks
    const userInfo = app.user?.attributes?.userInfo || {};
    if (userInfo.companySize && (typeof userInfo.companySize === "string")) {
        identifyEvent.set("company_size", userInfo.companySize);
    }
    if (userInfo.presentationsType && Array.isArray(userInfo.presentationsType)) {
        identifyEvent.set("content_type", [...new Set(userInfo.presentationsType)].sort());
    }
    if (userInfo.userRole && (typeof userInfo.userRole === "string")) {
        identifyEvent.set("user_defined_role", userInfo.userRole);
    }
    if (userInfo.userType && (typeof userInfo.userType === "string")) {
        identifyEvent.set("user_type", userInfo.userType);
    }

    const workspaceIds = app.user?.workspacesMetadata?.map(workspace => workspace.id).filter(workspaceId => workspaceId !== "personal");
    identifyEvent.set("user_workspace_ids", workspaceIds || []);

    // We can add this event property now as it will be refreshed more than once per day
    trackState({ "user_age_days_at_event": getFullDaysBetween(user.metadata.createdAt, Date.now()) });

    amplitude.identify(identifyEvent);
}

function trackAmplitude(eventId, event) {
    const remappedKeys = { "current_plan": "current_plan_nickname" };
    let amplitudeEvent = _.mapKeys(
        event.eventProperty,
        (value, key) => remappedKeys[key] || key
    );

    if (window.releaseVersion && window.releaseVersion !== "unknown") {
        amplitudeEvent.release_version = window.releaseVersion;
    }

    amplitude.track(eventId, amplitudeEvent);
}

// Old legacy function, returns values that don't make any sense, but we have to keep it
// so we don't break any downstream analytics
async function determinePlanForAnalytics() {
    if (app.user) {
        await app.user.load();

        const teamWorkspace = app.user.workspacesMetadata.find(workspace => workspace.id !== "personal");
        if (teamWorkspace) {
            const { getLegacyRoleInWorkspace } = await import("js/controllers/WorkspaceController");
            return await getLegacyRoleInWorkspace(teamWorkspace.id);
        }

        const personalWorkspace = app.user.getPersonalWorkspaceMetadata();
        if (personalWorkspace) {
            const plan = personalWorkspace.plan;
            if (plan.tier === WorkspacePlanTier.BASIC) {
                return "basic";
            }
            if (plan.tier === WorkspacePlanTier.PRO) {
                if (plan.name === "Edu Pro") {
                    return "edu";
                }
                return "pro";
            }
        }
    }

    return "N/A";
}

export async function identify(user, extraProps, isNew) {
    if (window.debugAnalytics) {
        logger.info("identify()", { uid: user.uid });
    }

    const plan = await determinePlanForAnalytics();

    if (window.amplitudeApiKey) {
        initAmplitude(user);
    }

    if (!isCypress && isInternalBAIUser(user.email)) {
        LogRocket.init("1xdce1/beautifulai");
        LogRocket.identify(user.uid, {
            name: user.displayName || user.email,
            email: user.email,
            subscriptionType: plan
        });
    }

    const { claims } = await user.getIdTokenResult();
    if (claims.session_tracking_disabled) {
        logger.info(`identify() ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`, { uid: user.uid });
        return;
    }

    const userInfo = app.user?.get("userInfo") || {};

    window.dataLayer.push({ userId: user.uid });

    const { analyticsCurrentPlan } = await fetchAnalyticsPersonalPlan();

    if (window.zE) {
        window.zE(function() {
            window.zE.identify({
                email: user.email,
                name: user.displayName || user.email,
                // TODO: this is going to be incorrect, but Zendesk doesn't update it anyway
                // https://developer.zendesk.com/embeddables/docs/widget/core#identify
                currentPlan: analyticsCurrentPlan,
                plan,
                ...userInfo
            });
        });
    }

    if (window.Appcues) {
        const workspaceId = app.appController?.workspaceId || "personal";

        const workspacesMetadata = await workspacesApi.getWorkspacesMetadata({ ids: [workspaceId] });

        const { subscriptionState, subscriptionStripeProductName, name } = workspacesMetadata.workspaces[0];

        window.Appcues.identify(user.uid, {
            createdAt: user.metadata.creationTime,
            planTier: analyticsCurrentPlan,
            plan,
            stripePlanName: subscriptionStripeProductName,
            stripeSubscriptionStatus: subscriptionState?.subscriptionStatus,
            ...(workspaceId !== "personal" ? {
                organizationId: workspaceId,
                organizationName: name,
            } : { organizationId: null, organizationName: null }),
            firstName: user.displayName,
            email: user.email,
            user_migrated: true,
            ...userInfo
        });

        if (workspaceId !== "personal") {
            window.Appcues.group(workspaceId, {
                organizationId: workspaceId,
                organizationName: name,
                stripePlanName: subscriptionStripeProductName,
                stripeSubscriptionStatus: subscriptionState?.subscriptionStatus
            });
        }
    }
}

/**
 * Returns legacy analytics values based on personal workspace metadata.
 * This is used for analytics tracking and should not be used for any other purpose
 * @param {IWorkspaceMetadata} personalWorkspaceMetadata - The metadata of the personal workspace
 * @returns {Object} - An object containing the analytics current plan and the current plan
 */
export function getAnalyticsPersonalPlan(personalWorkspaceMetadata) {
    const currentPlan = personalWorkspaceMetadata.plan.type;

    if (personalWorkspaceMetadata.subscriptionState?.subscriptionStatus === "trialing") {
        return {
            analyticsCurrentPlan: "trial",
            currentPlan
        };
    } else if (currentPlan === WorkspacePlanType.PRO_LEGACY ||
               currentPlan === WorkspacePlanType.PRO) {
        return {
            analyticsCurrentPlan: "individual-pro",
            currentPlan
        };
    } else if (currentPlan === WorkspacePlanType.PRO_EDU) {
        return {
            analyticsCurrentPlan: "edu",
            currentPlan: "edu"
        };
    } else {
        return {
            analyticsCurrentPlan: "basic",
            currentPlan
        };
    }
}

export async function fetchAnalyticsPersonalPlan() {
    const { workspaces: personalMetadata } = await workspacesApi.getWorkspacesMetadata({ ids: ["personal"] });
    return getAnalyticsPersonalPlan(personalMetadata[0]);
}

/**
 * Update the state that is sent along with trackEvent().
 */
export function trackState(props) {
    const newState = _.merge({}, state, props);
    if (window.debugAnalytics && !_.isEqual(state, newState)) {
        logger.info("state", newState);
    }
    state = newState;
}

export function getState(propName) {
    return state[propName];
}

/**
 * Helper to recursively update any state variables which appear in event props.
 */
function updateState(obj, updates, path = []) {
    for (const [key, value1] of Object.entries(obj)) {
        const childPath = [...path, key];
        const value2 = updates[key];
        if (value1 === undefined || value1 === null) {
            continue;
        }
        if (typeof value1 == "object") {
            updateState(value1, value2, childPath);
        } else {
            if (value1 === value2) {
                if (window.debugAnalytics) {
                    logger.warn(`[ANALYTICS] "${childPath.join(".")}" is redundant - tracked in state`);
                }
            } else if (updates.hasOwnProperty(key)) {
                if (window.debugAnalytics) {
                    logger.info("state", { update: childPath.join(".") + "->" + value2 });
                }
                obj[key] = value2;
            }
        }
    }
}

/**
 * Track an event.
 */
export async function track(options = {}) {
    const user = auth().currentUser;
    if (user) {
        const { claims } = await user.getIdTokenResult();
        if (claims.session_tracking_disabled) {
            logger.info(`track() ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`, { uid: user.uid });
            return;
        }
    }

    // merge state into props
    const props = _.merge({}, state, options.props);
    const role = props.role;

    const eventId = options.category + ":" + options.action;

    // update any state variables that are changed in props
    updateState(state, options.props);

    if (window.debugAnalytics && !isCypress) {
        logger.info("track", { ...options, eventId, props });

        // Delay showing the snackbar so that mounting works properly in player view.
        setTimeout(
            () => ShowSnackBar({
                message: `[TRACKING EVENT] ${eventId}`
            }),
            500
        );
    }

    if (!props.workspace_id) {
        logger.warn(`[ANALYTICS] options.props.workspace_id required in all events - ${eventId}`);
    }

    const removePrefix = (obj, prefix) => {
        Object.keys(obj).forEach(key => {
            if (key.indexOf(prefix) === 0) {
                obj[key.slice(3)] = obj[key];
                delete obj[key];
            }
        });
    };

    const sp_props = { ...props };
    removePrefix(sp_props, "sp_");
    const pg_props = { ...props };
    removePrefix(pg_props, "pg_");

    const event = {
        event: eventId,
        eventCategory: options.category,
        eventAction: options.action,
        eventLabel: options.label,
        eventProperty: sp_props,
        eventValue: options.value,
        eventUserId: user ? user.uid : null,
        eventRole: role
    };
    const flushEvent = {
        event: "Flush",
        eventCategory: undefined,
        eventAction: undefined,
        eventLabel: undefined,
        eventProperty: undefined,
        eventValue: undefined,
        eventRole: undefined
    };

    if (options.audit && user) {
        delete pg_props.email;
        delete pg_props.team_id;
        delete pg_props.affected_user_id;
        delete pg_props.presentationId;
        delete pg_props.workspace_id;
        pg_props.label = options.label;
        pg_props.value = options.value;

        const columns = {
            category: options.category,
            action: options.action,
            team_id: props.team_id,
            affected_user_id: props.pg_affected_user_id,
            presentation_id: props.presentationId,
            workspace_id: props.workspace_id,
            props: JSON.stringify(pg_props),
            email: user.email,
        };

        Api.audit.post({
            namespace: "analytics",
            action: null,
            props: columns
        })
            .catch(err => {
                logger.error(err, "Api.audit.post() failed");
            });
    }
    trackAmplitude(eventId, event);

    if (window.dataLayer) {
        // flush the full event to prevent GTM from re-firing previous event
        window.dataLayer.push(flushEvent);
        window.dataLayer.push(event);
    }

    if (user && user.uid && window.Appcues) {
        window.Appcues.track(eventId, event);
    }
}

export function setUserProps(props) {
    props = props || {};

    if (window.debugAnalytics) {
        logger.info("setUserProps", props);
    }
}

export function incUserProps(props) {
    props = props || {};

    if (window.debugAnalytics) {
        logger.info("incUserProps", props);
    }
}

export function getDebugAnalyticsState() {
    return state;
}

export async function audit(namespace, action, properties) {
    if (!navigator.onLine) {
        return;
    }

    await Api.audit.post({
        namespace: namespace,
        action: action,
        props: properties
    })
        .catch(err => {
            logger.error(err, "Api.audit.post() failed");
        });
}
